import React from "react";
import { Order, CollectionProcessingStatus } from "../../../api/order";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSSwitch from "../../system-design/DSSwitch";
import DSChip from "../../system-design/DSChip";
import DSGrid from "../../system-design/DSGrid";

interface OrderBasicInfoProps {
  order: Order;
  onManualOverrideChange: (value: boolean) => void;
}

const OrderBasicInfo = ({
  order,
  onManualOverrideChange,
}: OrderBasicInfoProps) => {
  const getStatusChipColor = (status: CollectionProcessingStatus) => {
    switch (status) {
      case CollectionProcessingStatus.COMPLETED:
        return "success";
      case CollectionProcessingStatus.PROCESSING:
        return "info";
      case CollectionProcessingStatus.FAILED:
      case CollectionProcessingStatus.PARTIAL_FAILURE:
        return "error";
      default:
        return "warning";
    }
  };

  return (
    <>
      <DSTypography variant="h6" gutterBottom>
        Order Information
      </DSTypography>
      <DSGrid container spacing={2} sx={{ mb: 3 }}>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Order ID
            </DSTypography>
            <DSTypography variant="body1">{order.id}</DSTypography>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Order Name
            </DSTypography>
            <DSTypography variant="body1">{order.orderName}</DSTypography>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Customer Name
            </DSTypography>
            <DSTypography variant="body1">{order.customerName}</DSTypography>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Customer Email
            </DSTypography>
            <DSTypography variant="body1">{order.customerEmail}</DSTypography>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Order Creation Date
            </DSTypography>
            <DSTypography variant="body1">
              {order.orderCreationDate
                ? new Date(order.orderCreationDate).toLocaleString()
                : "-"}
            </DSTypography>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Number of Clocks
            </DSTypography>
            <DSTypography variant="body1">{order.clocksNumber}</DSTypography>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Collection Processing Status
            </DSTypography>
            <DSBox sx={{ mt: 1 }}>
              <DSChip
                label={order.collectionProcessingStatus}
                color={getStatusChipColor(order.collectionProcessingStatus)}
              />
            </DSBox>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Collection Email Sent
            </DSTypography>
            <DSBox sx={{ mt: 1 }}>
              <DSChip
                label={order.collectionEmailSent ? "Yes" : "No"}
                color={order.collectionEmailSent ? "success" : "default"}
              />
            </DSBox>
          </DSBox>
        </DSGrid>
        {order.collectionProcessedAt && (
          <DSGrid item xs={12} sm={6}>
            <DSBox>
              <DSTypography variant="subtitle2" color="textSecondary">
                Collection Processed At
              </DSTypography>
              <DSTypography variant="body1">
                {new Date(order.collectionProcessedAt).toLocaleString()}
              </DSTypography>
            </DSBox>
          </DSGrid>
        )}
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Cancelled
            </DSTypography>
            <DSBox sx={{ mt: 1 }}>
              <DSChip
                label={order.cancelled ? "Yes" : "No"}
                color={order.cancelled ? "error" : "success"}
              />
            </DSBox>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12} sm={6}>
          <DSBox>
            <DSTypography variant="subtitle2" color="textSecondary">
              Fulfilled
            </DSTypography>
            <DSBox sx={{ mt: 1 }}>
              <DSChip
                label={order.fulfilled ? "Yes" : "No"}
                color={order.fulfilled ? "success" : "default"}
              />
            </DSBox>
          </DSBox>
        </DSGrid>
        <DSGrid item xs={12}>
          <DSBox>
            <DSTypography
              variant="subtitle2"
              color="textSecondary"
              gutterBottom
            >
              Manual Override
            </DSTypography>
            <DSTypography
              variant="caption"
              color="textSecondary"
              display="block"
              sx={{ mb: 1 }}
            >
              (Mark if manually adjusted fields, to avoid alerts)
            </DSTypography>
            <DSSwitch
              checked={!!order.manualOverride}
              onChange={(event) => onManualOverrideChange(event.target.checked)}
            />
          </DSBox>
        </DSGrid>
      </DSGrid>
    </>
  );
};

export default OrderBasicInfo;
